import { FileText } from 'lucide-react'
import { cn } from '@/lib/utils'

export type OrderViewMode = 'sales-order' | 'customer-order'

function DocumentButton({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 rounded-md p-1.5 transition-colors hover:bg-white/40"
      aria-pressed={isActive}
    >
      <div
        className={cn(
          'relative flex h-11 w-9 items-center justify-center rounded-sm border bg-white transition-colors',
          isActive ? 'border-gray-900 shadow-sm' : 'border-gray-300',
        )}
      >
        <FileText className={cn('h-5 w-5 transition-colors', isActive ? 'text-gray-700' : 'text-gray-400')} />
      </div>
      <span
        className={cn(
          'text-[10px] font-medium leading-none',
          isActive ? 'text-on-canvas' : 'text-on-canvas-muted',
        )}
      >
        {label}
      </span>
    </button>
  )
}

/**
 * The two documents behind one raised order, as the purchase order page shows its own chain.
 *
 * The sales order is what the ERP holds and is what the page opens on. The customer order is what
 * we asked for, kept a click away rather than discarded: it is the record of what the customer
 * sent and of what we did to it before sending, which is the only place a discrepancy between the
 * two can be read.
 */
export function DocumentNav({
  mode,
  onSelect,
}: {
  mode: OrderViewMode
  onSelect: (mode: OrderViewMode) => void
}) {
  return (
    <div className="flex items-center gap-0">
      <DocumentButton
        label="Customer order"
        isActive={mode === 'customer-order'}
        onClick={() => onSelect('customer-order')}
      />
      <div className="mb-[1.35rem] h-px w-3 shrink-0 bg-on-canvas/25" />
      <DocumentButton
        label="Sales order"
        isActive={mode === 'sales-order'}
        onClick={() => onSelect('sales-order')}
      />
    </div>
  )
}
